import { type FC } from "react";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { type PresentationBannerColorType } from "components/footer/PresentationBanner";
import { t } from "i18n/i18n";
import { PillButton } from "mds/components/PillButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCourseCapabilitiesByCourseId } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectClassSessionByCourseId, selectCurrentCourseId } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { Link } from "utils/routing";
import { pageUrl, withQueryParams } from "utils/urls";

interface FollowButtonProps {
  color: PresentationBannerColorType;
  courseId?: string;
  showReturnText?: boolean;
}

export const FollowButton: FC<FollowButtonProps> = ({
  color,
  showReturnText = false,
  courseId: courseIdProp,
}) => {
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const courseId = courseIdProp || currentCourseId || "";
  const classSession = useAppSelector((state) => selectClassSessionByCourseId(state, courseId));
  const presentedPageId = classSession?.presented_page_id;
  const showShortText = !useIsMdOrLarger();
  const { canParticipateInCourse } = useCourseCapabilitiesByCourseId(courseId);
  const dispatch = useAppDispatch();

  if (!courseId || !presentedPageId) {
    return null;
  }

  const presentedPageURL = withQueryParams(pageUrl(courseId, presentedPageId), {
    [FOLLOWING_QUERY_PARAM]: "1",
  });

  const closeSidebar = () => {
    if (canParticipateInCourse) {
      dispatch(localStateActions.setIsSidebarOpen(false));
    }
  };

  return (
    <PillButton
      as={Link}
      eventName="Presentation - Follow along"
      kind={`banner-${color}`}
      size="s"
      to={presentedPageURL}
      onClick={closeSidebar}
    >
      {showReturnText
        ? t("common.return")
        : showShortText
          ? t("main_header.follow_short")
          : t("main_header.follow")}
    </PillButton>
  );
};
